import { useState } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, LabelList, PieChart, Pie, Cell } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const card = 'bg-white rounded-2xl border border-slate-200/70 shadow-[0_2px_10px_rgba(15,23,42,0.04)]';
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

export const statusOf = (occ, cap) => (occ <= 0 ? 'libre' : occ >= cap ? 'complet' : 'partiel');
const STATUS = {
  libre: { label: 'Libre', dot: '#16a34a', badge: 'bg-emerald-50 text-emerald-700' },
  partiel: { label: 'Partiel', dot: '#e3a325', badge: 'bg-amber-50 text-amber-700' },
  complet: { label: 'Complet', dot: '#dc2626', badge: 'bg-rose-50 text-rose-700' },
};

export function TypeCards({ items, active, onSelect, total }) {
  return (
    <div className={`${card} p-5`} data-testid="types-panneaux">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Types de panneaux</h2>
          <p className="text-sm text-slate-500">Cliquez sur un rectangle pour voir ses emplacements</p>
        </div>
        <span className="text-sm font-bold text-slate-800" data-testid="emplacements-total">{total} emplacements au total</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
        {items.map((t, i) => {
          const isActive = active === t.key;
          const p = pct(t.occupied, t.capacity);
          return (
            <button key={t.key} onClick={() => onSelect(t.key)} data-testid={`type-card-${t.key}`}
              style={{ animationDelay: `${i * 60}ms` }}
              className={`cs-rise text-left rounded-2xl p-5 text-white transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 ${isActive ? 'bg-[#1f3fc7] ring-4 ring-blue-200 shadow-[0_10px_30px_rgba(31,63,199,0.35)]' : 'bg-[#4b72ee] hover:bg-[#3f66e6]'}`}>
              <div className="flex items-baseline justify-between">
                <span className="text-4xl font-extrabold leading-none" data-testid={`type-card-count-${t.key}`}>{t.count}</span>
                <span className="text-sm text-white/85">emplacement{t.count > 1 ? 's' : ''}</span>
              </div>
              <div className="text-lg font-bold mt-2">{t.label}</div>
              <div className="h-1.5 rounded-full bg-white/30 mt-4 overflow-hidden">
                <div className="h-full rounded-full bg-white transition-[width] duration-700" style={{ width: `${Math.max(p, t.occupied ? 2 : 1)}%` }} />
              </div>
              <div className="text-sm text-white/85 mt-2" data-testid={`type-card-fill-${t.key}`}>{p} % rempli · {t.occupied} sur {t.capacity} places</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function SectionKpis({ capacity, occupied }) {
  const items = [['Capacité totale', capacity, 'kpi-capacite'], ['Occupées', occupied, 'kpi-occupees'], ['Libres', capacity - occupied, 'kpi-libres'], ['Taux de remplissage', `${pct(occupied, capacity)} %`, 'kpi-taux']];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
      {items.map(([l, v, id]) => (
        <div key={id} className="rounded-xl border border-slate-200 px-4 py-3" data-testid={id}>
          <div className="text-sm text-slate-500">{l}</div>
          <div className="text-3xl font-extrabold text-[#2448d8] leading-tight">{v}</div>
        </div>
      ))}
    </div>
  );
}

export function FillRow({ e, num, occ, cap, isExample, onOpen, nameSlot, actions }) {
  const st = STATUS[statusOf(occ, cap)];
  const p = pct(occ, cap);
  return (
    <div onClick={isExample ? undefined : onOpen} data-testid={isExample ? `emplacement-example-${num}` : `emplacement-row-${num}`}
      className={`grid grid-cols-[32px_minmax(0,1fr)_170px_70px_70px_64px] gap-3 items-center px-3 py-3 border-b border-slate-100 transition-colors ${isExample ? '' : 'cursor-pointer hover:bg-blue-50/50'}`}>
      <div className="w-7 h-7 rounded-full bg-[#2448d8] text-white text-xs font-bold flex items-center justify-center">{num}</div>
      <div className="min-w-0 text-sm font-bold text-slate-900">
        {nameSlot || <span className="break-words">{e.nom}</span>}
        {isExample && <span className="ml-2 text-xs font-normal text-slate-400">exemple</span>}
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-[#2448d8]" style={{ width: `${p}%` }} /></div>
        <span className="text-sm font-bold text-slate-800 tabular-nums w-11">{occ}/{cap}</span>
        <span className="text-[11px] text-slate-400 w-9 text-right">{p} %</span>
      </div>
      <div className="text-sm text-slate-800"><b>{cap - occ}</b> libres</div>
      <div><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${st.badge}`} data-testid={`status-${num}`}>{st.label}</span></div>
      <div className="flex items-center justify-end gap-1" onClick={(ev) => ev.stopPropagation()}>{actions}</div>
    </div>
  );
}

export const TableHeader = () => (
  <div className="grid grid-cols-[32px_minmax(0,1fr)_170px_70px_70px_64px] gap-3 px-3 py-2 rounded-lg bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500">
    <div>N°</div><div>Emplacement</div><div>Remplissage</div><div>Libres</div><div>Statut</div><div className="text-right">Actions</div>
  </div>
);

const ROADS = [
  'left-0 top-[28%] w-[140%] h-3 -rotate-[8deg] origin-left', 'left-0 top-[70%] w-[140%] h-3 -rotate-[14deg] origin-left',
  'left-[16%] top-0 h-[120%] w-3 -rotate-[5deg] origin-top', 'left-[45%] top-0 h-[120%] w-3 rotate-[4deg] origin-top',
  'left-[68%] top-0 h-[120%] w-3 -rotate-[8deg] origin-top', 'right-[-10%] top-[10%] w-[60%] h-3 -rotate-[40deg] origin-right',
];

const positions = (rows) => {
  const raw = rawPositions(rows);
  if (rows.length > 4) return raw;
  return raw.map(({ x, y }) => ({ x: 8 + x * 0.6, y }));
};

const rawPositions = (rows) => {
  const pts = rows.map((r) => [Number(r.e?.latitude), Number(r.e?.longitude)]);
  const valid = pts.every(([a, b]) => a && b);
  const lats = pts.map((p) => p[0]); const lngs = pts.map((p) => p[1]);
  const [minA, maxA, minB, maxB] = [Math.min(...lats), Math.max(...lats), Math.min(...lngs), Math.max(...lngs)];
  return rows.map((r, i) => {
    if (valid && maxA - minA > 0.0005 && maxB - minB > 0.0005) {
      return { x: 10 + ((pts[i][1] - minB) / (maxB - minB)) * 75, y: 8 + ((maxA - pts[i][0]) / (maxA - minA)) * 80 };
    }
    return { x: 12 + ((i * 37) % 76), y: 8 + ((i * 53 + 17) % 82) };
  });
};

function MapCanvas({ rows, onOpen, tall }) {
  const pos = positions(rows);
  const showLabels = rows.length <= 4;
  return (
    <div className={`relative w-full ${tall ? 'h-[70vh]' : 'h-[440px]'} rounded-xl bg-[#eef1f5] overflow-hidden`} data-testid="schematic-map">
      {ROADS.map((c, i) => <div key={i} className={`absolute bg-white ${c}`} />)}
      {rows.map((r, i) => {
        const st = STATUS[statusOf(r.occ, r.cap)];
        return (
          <button key={r.key} onClick={() => !r.isExample && onOpen(r.e)} title={r.name}
            className="absolute -translate-y-1/2 flex items-center gap-1.5 group" style={{ left: `${pos[i].x}%`, top: `${pos[i].y}%` }}
            data-testid={`map-pin-${r.num}`}>
            <span className="w-8 h-8 rounded-full border-[3px] border-white shadow-md text-white text-xs font-bold flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: st.dot }}>{r.num}</span>
            {showLabels && <span className="text-sm font-bold text-slate-900 whitespace-nowrap">{r.name}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function SchematicMap({ title, rows, onOpen }) {
  const [full, setFull] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-slate-900">Carte · {title}</h3>
        <button onClick={() => setFull(true)} className="text-sm font-bold text-[#2448d8] hover:underline" data-testid="map-fullscreen-btn">Plein écran</button>
      </div>
      <MapCanvas rows={rows} onOpen={onOpen} />
      <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
        {Object.values(STATUS).map((s) => <span key={s.label} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.dot }} />{s.label}</span>)}
      </div>
      <p className="text-xs text-slate-400 mt-1">Aperçu schématique : positions indicatives</p>
      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent className="max-w-6xl w-[95vw]" data-testid="map-fullscreen-dialog">
          <DialogHeader><DialogTitle>Carte · {title}</DialogTitle></DialogHeader>
          <MapCanvas rows={rows} onOpen={(e) => { setFull(false); onOpen(e); }} tall />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function EvolutionCard({ points }) {
  const delta = points.length ? points[points.length - 1].value - points[0].value : 0;
  const first = points[0]?.mois?.toLowerCase(); const last = points[points.length - 1]?.mois?.toLowerCase();
  return (
    <div className={`${card} p-5`} data-testid="evolution-occupation">
      <h3 className="text-lg font-bold text-slate-900">Évolution de l'occupation</h3>
      <p className="text-sm text-slate-500">Places occupées par mois, de {first} à {last} {new Date().getFullYear()} · <b className="text-[#2448d8]">{delta >= 0 ? '+' : ''}{delta} depuis {first}</b></p>
      <div className="h-64 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 22, right: 16, left: -16, bottom: 0 }}>
            <CartesianGrid stroke="#eef1f5" vertical={false} />
            <XAxis dataKey="mois" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} domain={[0, (max) => Math.max(4, Math.ceil((max * 1.2) / 4) * 4)]} tickCount={5} />
            <Area type="linear" dataKey="value" stroke="#2448d8" strokeWidth={2.5} fill="#2448d8" fillOpacity={0.1} dot={{ r: 4, fill: '#fff', stroke: '#2448d8', strokeWidth: 2 }}>
              <LabelList dataKey="value" position="top" style={{ fontSize: 12, fontWeight: 700, fill: '#0f172a' }} />
            </Area>
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function TauxCard({ occupied, capacity }) {
  const free = capacity - occupied;
  return (
    <div className={`${card} p-5`} data-testid="taux-remplissage">
      <h3 className="text-lg font-bold text-slate-900">Taux de remplissage</h3>
      <p className="text-sm text-slate-500">Occupé ou libre aujourd'hui</p>
      <div className="relative h-52 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={[{ v: occupied }, { v: Math.max(free, 0) }]} dataKey="v" innerRadius="72%" outerRadius="92%" startAngle={90} endAngle={-270} stroke="none" cornerRadius={occupied && free ? 12 : 0}>
              <Cell fill="#2448d8" /><Cell fill="#eef1f5" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-4xl font-extrabold text-slate-900" data-testid="taux-value">{pct(occupied, capacity)} %</span>
          <span className="text-sm text-slate-500">rempli</span>
        </div>
      </div>
      <div className="flex justify-center gap-5 text-sm text-slate-600 mt-2">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#2448d8]" />Occupé {occupied}</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#e2e8f0]" />Libre {free}</span>
      </div>
    </div>
  );
}

const Line = ({ label, value, sub, green, testid }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-slate-100 last:border-0" data-testid={testid}>
    <span className="text-sm text-slate-600">{label}</span>
    <span className="text-right">
      <span className={`block text-sm font-bold ${green ? 'text-emerald-600' : 'text-slate-900'}`}>{value}</span>
      {sub && <span className="block text-xs text-slate-500">{sub}</span>}
    </span>
  </div>
);

export function ARetenirCard({ rows, points, occupied, capacity }) {
  const real = rows.filter((r) => !r.isExample);
  const pool = real.length ? real : rows;
  const most = [...pool].sort((a, b) => b.occ - a.occ)[0];
  const freest = [...pool].sort((a, b) => (b.cap - b.occ) - (a.cap - a.occ))[0];
  const delta = points.length ? points[points.length - 1].value - points[0].value : 0;
  return (
    <div className={`${card} p-5 pb-16`} data-testid="a-retenir">
      <h3 className="text-lg font-bold text-slate-900">À retenir</h3>
      <p className="text-sm text-slate-500 mb-2">Pour guider votre commercial</p>
      {most && <Line label="Le plus rempli" value={most.occ ? most.name : 'Aucun contrat pour l\u2019instant'} sub={most.occ ? `${most.occ}/${most.cap}` : ''} testid="retenir-plus-rempli" />}
      {freest && <Line label="Le plus de places libres" value={freest.name} sub={`${freest.cap - freest.occ} libres`} testid="retenir-plus-libres" />}
      <Line label="Évolution sur 6 mois" value={`${delta >= 0 ? '+' : ''}${delta} places`} green={delta > 0} testid="retenir-evolution" />
      <Line label="Places libres au total" value={`${capacity - occupied} sur ${capacity}`} testid="retenir-libres-total" />
    </div>
  );
}
